import { useEffect, useRef, useState } from "react";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/Field";
import {
  composeTimesheetReminderMail,
  getPublicLinks,
  sendTimesheetMail,
} from "@/features/timesheetFlow/timesheetFlowApi";
import type { TimesheetRecord } from "@/features/timesheetFlow/timesheetFlowTypes";
import { resolveTimesheetMailPdfUrl } from "@/pages/stundennachweise/timesheetFilterLogic";
import { useTimesheetFlowLayers } from "@/features/timesheetFlow/timesheetFlowLayers";

function mergeMailLists(current: string, extra: string): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of `${current},${extra}`.split(/[,;]/)) {
    const mail = part.trim();
    const key = mail.toLowerCase();
    if (!mail || seen.has(key)) continue;
    seen.add(key);
    out.push(mail);
  }
  return out.join(", ");
}

export function TimesheetReminderMailDialog({
  open,
  timesheetId,
  timesheet,
  displayNumber,
  recipients,
  cc: ccInitial = "",
  onClose,
  onSent,
  onError,
}: {
  open: boolean;
  timesheetId: number;
  timesheet: TimesheetRecord;
  displayNumber: string;
  recipients: string;
  cc?: string;
  onClose: () => void;
  onSent: () => void | Promise<void>;
  onError?: (message: string) => void;
}) {
  const layers = useTimesheetFlowLayers();
  const [to, setTo] = useState(recipients);
  const [cc, setCc] = useState(ccInitial);
  const [subject, setSubject] = useState(`Erinnerung: Stundennachweis ${displayNumber}`);
  const [bodyHtml, setBodyHtml] = useState("");
  const [sentOn, setSentOn] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [baseline, setBaseline] = useState({ to: "", cc: "", subject: "", bodyHtml: "" });
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setTo(recipients);
    setCc(ccInitial);
  }, [open, recipients, ccInitial]);

  useEffect(() => {
    if (!open || !timesheetId) return;
    let cancelled = false;
    setLoading(true);
    void composeTimesheetReminderMail(timesheetId, recipients)
      .then((data) => {
        if (cancelled) return;
        const nextCc = mergeMailLists(ccInitial, data.cc || "");
        setCc(nextCc);
        setSubject(data.subject || `Erinnerung: Stundennachweis ${displayNumber}`);
        setBodyHtml(data.html_body || "");
        setSentOn(data.sent_on ?? null);
        setBaseline({
          to: recipients,
          cc: nextCc,
          subject: data.subject || `Erinnerung: Stundennachweis ${displayNumber}`,
          bodyHtml: data.html_body || "",
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        onError?.(err instanceof Error ? err.message : "Mail-Vorschau konnte nicht geladen werden.");
        onClose();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, timesheetId, displayNumber, recipients, ccInitial, onClose, onError]);

  useEffect(() => {
    if (!open || !editorRef.current || loading) return;
    editorRef.current.innerHTML = bodyHtml;
  }, [open, bodyHtml, loading]);

  function readBodyHtml(): string {
    if (editorRef.current) return editorRef.current.innerHTML;
    return bodyHtml;
  }

  async function handleSend() {
    const toList = to
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!toList.length) {
      onError?.("Bitte mindestens einen Empfänger angeben.");
      return;
    }
    const html = readBodyHtml().trim();
    if (!html) {
      onError?.("Bitte einen Mailtext eingeben.");
      return;
    }
    setSending(true);
    try {
      const links = await getPublicLinks(timesheetId);
      const pdfUrl = resolveTimesheetMailPdfUrl(timesheet, links, { reminder: true });
      const ccList = cc
        .split(/[,;]/)
        .map((s) => s.trim())
        .filter(Boolean);
      await sendTimesheetMail(timesheetId, {
        recipients: toList,
        cc: ccList,
        subject: subject.trim() || `Erinnerung: Stundennachweis ${displayNumber}`,
        pdf_url: pdfUrl,
        html_body: html,
        reminder: true,
        mark_contact: false,
      });
      await onSent();
      onClose();
    } catch (err: unknown) {
      onError?.(err instanceof Error ? err.message : "Erinnerungsmail konnte nicht versendet werden.");
    } finally {
      setSending(false);
    }
  }

  const dirty =
    !loading &&
    (to !== baseline.to ||
      cc !== baseline.cc ||
      subject !== baseline.subject ||
      bodyHtml !== baseline.bodyHtml);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={dirty}
      title="Erinnerungsmail"
      size="lg"
      zIndexClass={layers.deep}
      footer={
        <>
          <DialogCancelButton onClose={onClose} disabled={sending} />
          <Button type="button" disabled={loading || sending} onClick={() => void handleSend()}>
            {sending ? "Senden …" : "Erinnerungsmail senden"}
          </Button>
        </>
      }
    >
      {loading ? (
        <p className="text-sm text-label-secondary">Mail-Vorschau wird geladen …</p>
      ) : (
        <>
          {sentOn ? (
            <p className="mb-3 text-xs text-label-secondary">
              Ursprünglicher Versand am {sentOn} — Datum im Text kann Sie bei Bedarf anpassen.
            </p>
          ) : (
            <p className="mb-3 text-xs text-label-tertiary">
              Kein früherer Mail-Versand im Protokoll gefunden; der Text verweist ohne konkretes Datum
              auf den bereits zugesandten Nachweis.
            </p>
          )}
          <TextInput label="An" value={to} onChange={(e) => setTo(e.target.value)} className="mb-2" />
          <TextInput
            label="CC"
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            placeholder="optional"
            hint="Team-Mail für die Erinnerung, sofern hinterlegt."
            className="mb-2"
          />
          <TextInput
            label="Betreff"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="mb-2"
          />
          <label className="mb-1 block text-xs font-medium text-label-secondary">Nachricht (HTML)</label>
          <div
            ref={editorRef}
            className="eh-mail-html-editor mb-1 min-h-[280px] w-full text-sm"
            contentEditable={!sending}
            suppressContentEditableWarning
            onInput={() => {
              if (editorRef.current) setBodyHtml(editorRef.current.innerHTML);
            }}
          />
          <p className="text-xs text-label-tertiary">
            Der Stundennachweis wird wie gewohnt als PDF angehängt. Der NW-Zähler wird nicht zurückgesetzt.
          </p>
        </>
      )}
    </Dialog>
  );
}
